import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RiCloseLine } from "react-icons/ri";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import ErrorMsg from "../../Utility/ErrorMsg/ErrorMsg";
import { logger } from "../../utils/logger";
import { AddTeamSchema } from "../../FormsValidations/Validations";
import { updateTeam } from "../../Redux/Thunks/teamsThunk";
import { formToTeamUpdatePayload } from "./teamsDataUtils";
import { showSuccess } from "../../utils/toast";
import { getThunkRejectMessage } from "../../utils/thunkErrorMessage";
import { isGlobalApiErrorModalOpen } from "../../utils/globalApiErrorModal";
import "../Clients/clients.css";

const EditTeam = ({ isOpen, onClose, onSuccess, team }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddTeamSchema),
    defaultValues: {
      teamName: "",
      status: "",
    },
  });

  // Map API status to display (ACTIVE → Active, INACTIVE → Inactive)
  const statusForDisplay = (s) => {
    if (!s) return "Active";
    const map = {
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      Active: "Active",
      Inactive: "Inactive",
      "In-Active": "Inactive",
    };
    return map[s] ?? "Active";
  };

  useEffect(() => {
    if (isOpen && team) {
      reset({
        teamName: team.teamName ?? team.name ?? "",
        status: statusForDisplay(team.status),
      });
    }
  }, [isOpen, team, reset]);

  const onSubmit = async (data) => {
    if (!team?.id) return;
    setLoading(true);
    try {
      await dispatch(
        updateTeam({
          id: team.id,
          teamData: formToTeamUpdatePayload(data),
        })
      ).unwrap();
      showSuccess("Team updated successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      logger.error("Error updating team:", error);
      if (isGlobalApiErrorModalOpen()) return;
      setError("submit", {
        type: "manual",
        message: getThunkRejectMessage(error, "Failed to update team. Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <div
          className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-border-primary">
            <Typography variant="h5" className="font-semibold text-text-primary">
              Edit Team
            </Typography>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={loading}
              className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close modal"
            >
              <RiCloseLine className="w-5 h-5" />
            </Button>
          </div>

          <div className="p-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="edit-teamName"
                  className="font-medium text-text-primary mb-1 block"
                >
                  Team Name
                  <Typography variant="span1" className="text-red-600 pl-1" component="span">
                    *
                  </Typography>
                </Typography>
                <Input
                  type="text"
                  {...register("teamName")}
                  className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                    errors.teamName ? "border-badge-danger" : ""
                  }`}
                  id="edit-teamName"
                  placeholder="Enter team name"
                  label=""
                  error={errors.teamName?.message}
                  noWrapper={true}
                  disabled={loading}
                />
                <ErrorMsg message={errors.teamName?.message} className="mt-1" />
              </div>

              <div className="mb-4">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      label="Status"
                      options={["Active", "Inactive"]}
                      placeholder="Select Status"
                      error={errors.status?.message}
                      disabled={loading}
                      required={true}
                      searchable={false}
                      className="text-sm"
                    />
                  )}
                />
              </div>

              {errors.submit && <ErrorMsg message={errors.submit.message} className="mb-4" />}

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="danger"
                  size="md"
                  disabled={loading}
                  onClick={handleClose}
                  className="btn-reset"
                >
                  Cancel
                </Button>
                <FormSubmitButton
                  loading={loading}
                  label="Update Team"
                  idleIconClass="ri-save-line"
                  size="md"
                  className="inline-block text-white font-bold transition-colors"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditTeam;
